import { type ApprovalRequestId, type ProviderApprovalDecision } from "@cleo/contracts";
import { useNavigate } from "@tanstack/react-router";
import { memo, useState } from "react";

import { type PendingApproval } from "../../session-logic";
import { Button } from "../ui/button";
import { useApprovalRequestContext } from "~/neucleos/approvalRequestContext";
import { describeApproval } from "./approvalPresentation";
import { revealedRequestText } from "../RequestText";
import {
  calledTool,
  SUBSCRIPTION_CANCEL_GATE_KEY,
  SUBSCRIPTION_SCHEDULE_TOOL,
} from "~/neucleos/gateInstancesClient";
import { useTouchIdApproval } from "~/neucleos/subscriptionTouchIdClient";
import { openWorldNotice } from "~/neucleos/openWorldNotice";
import { openWorldViewInboxRail } from "~/neucleos/worldViewController";
import {
  PRESSED_LABEL,
  type PressedDecision,
  approvalPressKey,
  decisionRefused,
  useApprovalPress,
} from "../approvalPress";

/** Why Approve waits: the card's details are still being read and none is shown yet. */
export const APPROVAL_DETAILS_UNSEEN =
  "Approve is available once the card shows what this request does.";

interface ComposerPendingApprovalActionsProps {
  approval: PendingApproval;
  isResponding: boolean;
  onCancelTurn: () => void;
  onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<unknown>;
  /** The conversation's World, so the headline names what the request affects. */
  worldKey?: string | null;
}

/**
 * The four decisions, unchanged in meaning: cancel the turn, decline, allow
 * this session, allow once. The thumbs-up approves once
 * and carries the headline in its accessible name, so
 * the person re-reads what they are about to cause as they decide. A request
 * the person decides every time (sessionAllow false: a protected place) has
 * no session-wide answer, so its card offers none; the server enforces it too.
 */
export const ComposerPendingApprovalActions = memo(function ComposerPendingApprovalActions({
  approval,
  isResponding,
  onCancelTurn,
  onRespondToApproval,
  worldKey = null,
}: ComposerPendingApprovalActionsProps) {
  const named = useApprovalRequestContext(
    worldKey,
    approval.detail ? { detail: approval.detail, call: approval.call ?? null } : null,
  );
  const shown = describeApproval(approval, named);
  // Stopping a subscription renewing is approved only with Touch ID over the
  // exact words, through the World card this approval waits on.
  const touchIdCard =
    approval.gateKey === SUBSCRIPTION_CANCEL_GATE_KEY ||
    calledTool(approval.call) === SUBSCRIPTION_SCHEDULE_TOOL;
  const touchId = useTouchIdApproval(touchIdCard, worldKey, approval.gateInstanceKey ?? null);
  const blocked =
    shown.approvalBlocked ??
    (touchIdCard && !approval.gateInstanceKey
      ? "This approval is not linked to its World card, so it cannot be approved here."
      : touchId.unavailable);
  const once = touchIdCard ? "Approve with Touch ID" : "Approve once";
  // A card whose details are still being read shows none of them: nothing is approved unseen.
  const unseen =
    shown.plainDetail !== null &&
    shown.plainDetail.length === 0 &&
    (approval.detail ?? "").trim() !== ""
      ? APPROVAL_DETAILS_UNSEEN
      : null;
  // W-8af4eaef: the press shows in the click's own commit. A decision refused
  // when sent, or never sent, releases it; so does the provider's later
  // refusal of the answer (approval.respondFailureId: the conversation shows
  // its error). A landed decision closes the card.
  const { pressed, press, release } = useApprovalPress(
    approvalPressKey("provider", approval.requestId),
    approval.respondFailureId ?? null,
  );
  const respond = (decision: ProviderApprovalDecision) => {
    const pressedAs: PressedDecision = decision === "decline" ? "decline" : "approve";
    if (!press(pressedAs, decision)) return;
    const send = () =>
      void onRespondToApproval(approval.requestId, decision).then(
        (result) => {
          if (decisionRefused(result)) release();
        },
        () => release(),
      );
    // A card whose subject can move under it (a platform Components release) reads it once more before Approve acts;
    // moved, the press is released and the card, redrawn from that read, says why.
    const recheck = decision === "decline" ? null : named.recheck();
    if (recheck === null) return send();
    void recheck.then((why) => (why === null ? send() : release()));
  };
  const deciding = isResponding || pressed !== null;
  const pressedOn = (decision: ProviderApprovalDecision) =>
    pressed?.button === decision ? PRESSED_LABEL[pressed.decision] : null;
  return (
    <>
      <Button size="sm" variant="ghost" disabled={isResponding} onClick={onCancelTurn}>
        Cancel turn
      </Button>
      <Button
        size="sm"
        variant="destructive-outline"
        disabled={deciding}
        aria-busy={pressedOn("decline") !== null || undefined}
        onClick={() => respond("decline")}
      >
        <span aria-hidden="true">👎</span> {pressedOn("decline") ?? "Decline"}
      </Button>
      {approval.sessionAllow !== true || shown.perCallOnly ? null : (
        <Button
          size="sm"
          variant="outline"
          // Every accept waits for the card's details, the session-wide one too.
          disabled={deciding || unseen !== null}
          title={unseen ?? undefined}
          aria-busy={pressedOn("acceptForSession") !== null || undefined}
          onClick={() => respond("acceptForSession")}
        >
          {pressedOn("acceptForSession") ?? "Always allow this session"}
        </Button>
      )}
      {shown.approveOnCard === true ? (
        <OpenMembersCardButton
          worldKey={worldKey}
          gateInstanceKey={approval.gateInstanceKey ?? null}
          disabled={deciding}
        />
      ) : (
        <Button
          size="sm"
          variant="default"
          disabled={
            deciding || blocked !== null || unseen !== null || touchId.pending || touchId.checking
          }
          aria-busy={pressedOn("accept") !== null || undefined}
          aria-label={`${pressedOn("accept") ?? once}: ${revealedRequestText(shown.headline)}`}
          title={blocked ?? unseen ?? touchId.message ?? undefined}
          onClick={() =>
            touchIdCard
              ? // The World records the Touch ID approval; the conversation then
                // reads that decision back before the call runs.
                void touchId.approve().then((landed) => {
                  if (landed) respond("accept");
                })
              : respond("accept")
          }
        >
          <span aria-hidden="true">👍</span> {pressedOn("accept") ?? once}
        </Button>
      )}
      {touchIdCard &&
      (touchId.pending ||
        touchId.message !== null ||
        (blocked !== null && shown.approvalBlocked == null)) ? (
        <span className="text-xs text-warning" data-approval-touch-id="" role="status">
          {touchId.pending ? "Waiting for Touch ID…" : (touchId.message ?? blocked)}
        </span>
      ) : null}
    </>
  );
});

/** W-786fe139: an Approve that has no card of the World to open (an older record not linked to its World card). */
export const MEMBERS_CARD_NOT_LINKED =
  "This approval is not linked to its Members card. Open your Inbox and approve it there.";

/**
 * W-786fe139 (the Lead's ruling A): the Approve of a change to who belongs to a World that is approved with Touch ID.
 * It decides nothing: it opens that change's Members card in the Inbox (the room's own Inbox when one is seated, else
 * the World's decisions door), whose Approve asks for Touch ID. The conversation keeps waiting on the card, and the
 * card's decision answers it (worldGateBridge watchWorldGateInstance).
 */
export function OpenMembersCardButton({
  worldKey,
  gateInstanceKey,
  disabled,
}: {
  readonly worldKey: string | null;
  readonly gateInstanceKey: string | null;
  readonly disabled: boolean;
}) {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const linked = worldKey !== null && gateInstanceKey !== null;
  const open = () => {
    if (!linked || opening) return;
    setProblem(null);
    if (openWorldViewInboxRail("decisions", gateInstanceKey)) return;
    setOpening(true);
    void openWorldNotice(worldKey, { kind: "decisions", id: gateInstanceKey }, navigate)
      .catch((cause: unknown) =>
        setProblem(
          cause instanceof Error ? cause.message : "Its Members card could not be opened.",
        ),
      )
      .finally(() => setOpening(false));
  };
  return (
    <>
      <Button
        size="sm"
        variant="default"
        disabled={disabled || !linked || opening}
        data-approval-on-card=""
        title={linked ? undefined : MEMBERS_CARD_NOT_LINKED}
        onClick={open}
      >
        <span aria-hidden="true">👍</span> Approve on its Members card
      </Button>
      {problem !== null || !linked ? (
        <span className="text-xs text-warning" role="status">
          {problem ?? MEMBERS_CARD_NOT_LINKED}
        </span>
      ) : null}
    </>
  );
}
